/**
 * What the user-management screen offers for a user's role, disbursement limit, permissions
 * and payment-plan limits. The backend decides (`services/access_change_policy.py`); this
 * only keeps the screen from offering what it would refuse, so the refusal is the exception.
 *
 * - Nobody edits their own role, limit, permissions or payment-plan limits.
 * - Raising a limit, and granting `can_disburse`, `can_approve_disbursement` or
 *   `can_manage_roles`, is Admin only. Reducing access is not.
 * - A role is judged by its own permission set: a non-Admin cannot assign `Admin` or a role
 *   holding any of those, whatever the person already holds.
 * - A non-Admin ticks only permissions they hold themselves, and cannot change an Admin's
 *   account.
 * - Removing or loosening payment-plan limits is Admin only.
 */

/** The permissions whose grant is Admin only for money. */
export const MONEY_PERMISSIONS = ["can_disburse", "can_approve_disbursement"];

/** Everything a non-Admin may not hand out. `can_manage_roles` edits every holder's role. */
export const PRIVILEGED_PERMISSIONS = [...MONEY_PERMISSIONS, "can_manage_roles"];

const ADMIN_ROLE = "Admin";

/** Shown beside the locked controls on the caller's own row. */
export const SELF_EDIT_NOTE =
  "You can't change your own role, disbursement limit, permissions or payment plan limits. Ask another Admin.";

/** Shown beside the controls an Admin-only grant is withheld from. */
export const MONEY_GRANT_NOTE =
  "Only an Admin can raise a limit, or grant disbursement permissions or role management, or assign a role that carries them.";

/** Shown when a non-Admin opens an Admin's row. */
export const ADMIN_ACCOUNT_NOTE =
  "Only an Admin can change an Admin's role, limit, permissions or payment plan limits.";

/** Shown when a non-Admin tries to remove or loosen payment-plan limits. */
export const PP_LIMITS_NOTE =
  "Only an Admin can remove or loosen payment plan limits. You can tighten them.";

/**
 * True when `target` is the signed-in user's own record, by id or by email.
 *
 * @param {{user_id?: string, email?: string}|null} me the signed-in user.
 * @param {{user_id?: string, email?: string}|null} target the row being edited.
 * @returns {boolean}
 */
export function isOwnRow(me, target) {
  if (!me || !target) return false;
  if (me.user_id && target.user_id && me.user_id === target.user_id) return true;
  const mine = (me.email || "").trim().toLowerCase();
  const theirs = (target.email || "").trim().toLowerCase();
  return Boolean(mine) && mine === theirs;
}

/**
 * The privileged permissions in `permissions` that the user does not already hold.
 *
 * @param {string[]} permissions what would be granted.
 * @param {string[]} held what the target holds today.
 * @returns {string[]}
 */
export function newPrivilegedPermissions(permissions, held = []) {
  return PRIVILEGED_PERMISSIONS.filter(
    (p) => (permissions || []).includes(p) && !(held || []).includes(p),
  );
}

/**
 * Whether a role may be offered to the caller. A role is judged by its own permission
 * set, not by what it adds for this target: `Admin`, and any role holding a privileged
 * permission, are Admin only, so a peer who already holds both money permissions cannot be
 * promoted. That also withholds moving someone between two such roles, even downward.
 *
 * @param {boolean} isAdmin whether the caller is an Admin.
 * @param {{name: string, permissions: string[]}} role a roles-catalogue entry.
 * @returns {boolean}
 */
export function roleAssignableBy(isAdmin, role) {
  if (isAdmin) return true;
  if (role?.name === ADMIN_ROLE) return false;
  return !PRIVILEGED_PERMISSIONS.some((p) => (role?.permissions || []).includes(p));
}

/**
 * Whether a single permission may be ticked by the caller. A permission the target already
 * holds is not a grant. Otherwise a non-Admin may tick only one they hold themselves, and
 * never a privileged one.
 *
 * @param {boolean} isAdmin whether the caller is an Admin.
 * @param {string} permission the permission being ticked.
 * @param {string[]} heldPermissions the target's current effective permissions.
 * @param {string[]} callerPermissions the caller's own permissions.
 * @returns {boolean}
 */
export function permissionTickableBy(
  isAdmin,
  permission,
  heldPermissions = [],
  callerPermissions = [],
) {
  if (isAdmin) return true;
  if ((heldPermissions || []).includes(permission)) return true;
  if (PRIVILEGED_PERMISSIONS.includes(permission)) return false;
  return (callerPermissions || []).includes(permission);
}

/**
 * True when the record is an Admin's, which a non-Admin may not change.
 *
 * @param {{role?: string}|null} target the row being edited.
 * @returns {boolean}
 */
export function isAdminAccount(target) {
  return target?.role === ADMIN_ROLE;
}

const FLOOR_LIMITS = ["min_pay_pct"];
const CEILING_LIMITS = ["max_discount_pct", "max_duration_weeks"];
const LIMIT_KEYS = [...FLOOR_LIMITS, ...CEILING_LIMITS];

/**
 * The limits that are set, read the way the backend's approver gate reads stored ones: a
 * blank or non-numeric value is no limit, anything else is kept, so a stored 0 or negative
 * is still "has limits". Mirrors `stored_pp_limits` in `access_change_policy.py`.
 */
function setLimits(limits) {
  const out = {};
  for (const key of LIMIT_KEYS) {
    const raw = limits?.[key];
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (!Number.isNaN(n)) out[key] = n;
  }
  return out;
}

/**
 * The message for a limit the backend would refuse as input, or null. A minimum payment or a
 * duration of 0 reads as the tightest cap while enforcement skips it, and a negative duration
 * is a cap no plan can meet; a discount cap of 0 is real.
 *
 * @param {object} limits the limits the form would send.
 * @returns {string|null}
 */
export function ppLimitsError(limits) {
  const now = setLimits(limits);
  for (const key of ["min_pay_pct", "max_duration_weeks"]) {
    if (key in now && !(now[key] > 0)) {
      return "Minimum payment and maximum duration must be above 0. Leave a box blank for no limit.";
    }
  }
  if ("max_discount_pct" in now && !(now.max_discount_pct >= 0)) {
    return "The discount cap can't be negative.";
  }
  return null;
}

/** Whether `now` lets a user do what `was` did not, for one limit. Mirrors `_limit_loosened`. */
function limitLoosened(key, was, now) {
  if (now === was) return false;
  if (FLOOR_LIMITS.includes(key)) {
    if (was <= 0) return !Number.isFinite(now);
    return !(now >= was);
  }
  if (key === "max_duration_weeks") {
    if (was < 0) return true; // the strictest value: any change from it loosens
    if (was === 0) return !Number.isFinite(now);
    if (now < 0) return false;
    return !(now > 0 && now <= was); // 0 switches the cap off
  }
  if (was < 0) return true;
  return !(now <= was);
}

/**
 * Whether `after` removes or loosens payment-plan limits that `before` has stored. A limit
 * that is cleared is loosened, a stored 0 or negative included (having none is what makes
 * someone an approver); `min_pay_pct` is a floor, so lower is looser; a duration of 0
 * switches the cap off. A limit newly set is not loosened.
 *
 * @param {object|null} before the limits stored today.
 * @param {object|null} after the limits the form would send.
 * @returns {boolean}
 */
export function ppLimitsLoosened(before, after) {
  const was = setLimits(before);
  const now = setLimits(after);
  return Object.keys(was).some((key) => !(key in now) || limitLoosened(key, was[key], now[key]));
}

/**
 * Whether the form's payment-plan limits differ from the ones loaded for the user. A blank
 * box and an absent limit are the same, so an untouched form is "unchanged". The form sends
 * `pp_limits` only when this is true: re-sending an old stored value (a 0 or negative the
 * backend now refuses as input) would make that user impossible to edit for anything else.
 *
 * @param {object|null} before the limits loaded with the user.
 * @param {object|null} after the limits the form holds.
 * @returns {boolean}
 */
export function ppLimitsChanged(before, after) {
  const was = setLimits(before);
  const now = setLimits(after);
  const keys = new Set([...Object.keys(was), ...Object.keys(now)]);
  return [...keys].some((key) => was[key] !== now[key]);
}

/** Shown beside the limit boxes when a user's stored limit is one the form can no longer save. */
export const LEGACY_PP_LIMIT_NOTE =
  "This user has an old payment plan limit (0 or negative) that can no longer be saved as a new one. It can only be replaced, and only an Admin can replace it if that loosens the limits.";

/**
 * True when a stored limit is one the backend would now refuse as input, so it is shown as-is
 * but cannot be re-sent.
 *
 * @param {object|null} stored the limits loaded with the user.
 * @returns {boolean}
 */
export function hasLegacyPpLimit(stored) {
  return ppLimitsError(stored) !== null;
}

/**
 * Whether the limit box is empty.
 *
 * @param {string|number|null|undefined} boxValue what the input holds.
 * @returns {boolean}
 */
function limitBoxIsEmpty(boxValue) {
  return boxValue === "" || boxValue === null || boxValue === undefined;
}

/**
 * The disbursement limit to send for what the limit box shows.
 *
 * The backend reads `null` as "keep the stored limit", and on an invite as "use the role's
 * default". The box used to send `null` whenever it was empty while showing a grey `0`, so an
 * admin who cleared it to remove a limit saw £0, saved, and left the old limit in force.
 *
 * An emptied box now sends 0, the figure its placeholder shows. The one case that still sends
 * `null` is an edit of a user who has no stored limit and whose box was left empty: nothing was
 * decided and nothing was changed, and the Users table keeps "—" apart from an explicit £0.
 * `limitBoxPlaceholder` gives that case a placeholder that does not read as a number.
 *
 * @param {string|number|null|undefined} boxValue what the input holds.
 * @param {{isEdit?: boolean, storedLimit?: number|null}} [context] the user being edited.
 * @returns {number|null} the limit to send, or null to leave an unset limit unset.
 */
export function limitToStore(boxValue, { isEdit = false, storedLimit = null } = {}) {
  if (!limitBoxIsEmpty(boxValue)) return Number(boxValue);
  return isEdit && storedLimit == null ? null : 0;
}

/**
 * The placeholder for the limit box: what an empty box will be stored as.
 *
 * @param {{isEdit?: boolean, storedLimit?: number|null}} [context] the user being edited.
 * @returns {string}
 */
export function limitBoxPlaceholder({ isEdit = false, storedLimit = null } = {}) {
  return isEdit && storedLimit == null ? "Not set" : "0";
}

/**
 * A stored disbursement limit as the server returned it, for the save confirmation.
 *
 * @param {number|null|undefined} limit the limit in the save response.
 * @returns {string}
 */
export function storedLimitLabel(limit) {
  return limit == null ? "not set" : `£${Number(limit).toLocaleString()}`;
}
